import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import WelcomeOverlay from './components/WelcomeOverlay.tsx';
import EventCardSection from './components/EventCardSection.tsx';
import MainContent from './components/MainContent.tsx';
import QuickActions from './components/QuickActions.tsx';
import './App.css';
import { motion } from 'framer-motion';

const App: React.FC = () => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const rsvpSectionRef = useRef<HTMLElement>(null);

  // Invitation opening handler
  const handleOpenInvitation = () => {
    setOverlayOpen(true);
    // Start showing content immediately, not after delay
    setShowContent(true);
  };

  // Autoplay music when content shows
  useEffect(() => {
    if (showContent && audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          // Autoplay was prevented, user will need to click play
          console.log('Autoplay prevented:', error);
        });
    }
  }, [showContent]);

  // Music controls
  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Scroll to RSVP section
  const scrollToRSVP = () => {
    rsvpSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className="min-h-screen bg-[#0D204A] overflow-auto">
      {/* Background Music */}
      <audio 
        ref={audioRef} 
        loop 
        src="/audio/homecoming-song.mp3"
      />

      <AnimatePresence>
        {!overlayOpen && (
          <WelcomeOverlay onOpenInvitation={handleOpenInvitation} />
        )}
      </AnimatePresence>

      <div className="w-full max-w-[430px] mx-auto relative">
        {/* Event Card Section - Shows after overlay closes */}
        <EventCardSection showContent={showContent} />

        {/* Content - shows below event card */}
        {showContent && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="relative z-20 bg-[#0D204A]"
          >
            <MainContent rsvpSectionRef={rsvpSectionRef} />
          </motion.div>
        )}

        {/* Quick Actions - Shows after content is visible */}
        {showContent && (
          <QuickActions 
            isPlaying={isPlaying}
            toggleMusic={toggleMusic}
            scrollToRSVP={scrollToRSVP}
          />
        )}
      </div>
    </div>
  );
};

export default App; 